/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type {
  ITrickPlayConfig,
  ITrickPlayEligibility,
  SeekRangeMode
} from "../interfaces";

/**
 * Subset of session-derived flags that influence trick-play eligibility.
 * Mirrors the PRD §5 mode/SO matrix inputs.
 */
export interface ITrickPlaySessionFlags {
  /**
   * Whether a dedicated Start Over stream is available for this session.
   * Derived from `isAvailableInSocu` in the v4/preview response.
   *
   * - TMIS: When false, all trick-play is force-disabled.
   * - IPTV: When false, Start Over button seeks to buffer start (same stream)
   *   instead of switching to a dedicated SOCU stream.
   */
  startOverAvailable: boolean;

  /**
   * Whether SOCU (Start Over or Catch Up) is currently playing — playback is
   * already on the recording-host stream rather than the live/timeshift one.
   *
   * While SOCU plays, the Start Over and Go LIVE action buttons are stream-level
   * operations rather than seeks, so they stop depending on the mode matrix.
   * See {@link deriveEligibility} for the override this drives.
   */
  isSocuPlaying: boolean;

  /**
   * Whether an nDVR recording (in-progress or completed) is currently playing.
   *
   * Restart-to-beginning is always allowed for nDVR playback regardless of
   * `trickModeRestrictions.allowRewind` — mirrors mobi's `!isLive` restart rule.
   * Unlike SOCU, this does not force `goLiveAvailable`: Go LIVE is not
   * applicable to nDVR playback at all (hidden at the action-button layer).
   *
   * Optional — defaults to falsy for existing call sites/tests that predate
   * nDVR restart-eligibility support.
   */
  isNdvrPlaying?: boolean;

  /**
   * Whether VOD (on-demand) content is currently playing.
   *
   * Same restart-to-beginning override as nDVR, for the same reason: it is
   * on-demand content with no live edge, not a live/timeshift stream.
   *
   * Optional — defaults to falsy for existing call sites/tests that predate
   * VOD restart-eligibility support.
   */
  isVodPlaying?: boolean;

  /**
   * Whether the player's seekable range is wide enough to seek in: for live,
   * a timeshift buffer wider than `MIN_TIMESHIFT_WINDOW_SECONDS`; for Start
   * Over, Catch Up, nDVR and VOD, any non-empty range.
   *
   * Drives the TMIS seek-bar range when no Start Over stream is available —
   * see {@link deriveModeEligibility}. Ignored on IPTV.
   *
   * Optional — when absent, TMIS falls back to proxying it by
   * `trickModeRestrictions` (any of FF/RW allowed), the rule that predates
   * this signal.
   */
  hasSeekableWindow?: boolean;

  /**
   * Trick-mode restrictions returned by the session-create response.
   * `null` when the API failed, returned no value, or was unreachable —
   * the caller is expected to also call `setRestrictionEnabled(false)`
   * in that case (PRD §7.1 TP-AP-1..4).
   */
  trickModeRestrictions: ITrickModeRestrictions | null;
}

/**
 * The subset of `Session.trickModeRestrictions` consumed by eligibility.
 * Backend ships these as `Record<string, unknown>` — callers normalize.
 */
export interface ITrickModeRestrictions {
  /** Whether the user is allowed to fast-forward / skip forward / hold forward. */
  allowFastForward: boolean;

  /** Whether the user is allowed to rewind / skip backward / hold backward. */
  allowRewind: boolean;

  /** Whether the user is allowed to pause playback. */
  allowPause: boolean;
}

/**
 * Parse a raw `trickModeRestrictions` payload into a strongly-typed shape.
 * Returns `null` when the payload is missing or any required field is absent.
 */
export function parseTrickModeRestrictions(
  raw: Record<string, unknown> | null | undefined
): ITrickModeRestrictions | null {
  if (raw === null || raw === undefined) {
    return null;
  }
  const allowFastForward = raw.allowFastForward;
  const allowRewind = raw.allowRewind;
  const allowPause = raw.allowPause;
  if (
    typeof allowFastForward !== "boolean" ||
    typeof allowRewind !== "boolean" ||
    typeof allowPause !== "boolean"
  ) {
    return null;
  }
  return { allowFastForward, allowRewind, allowPause };
}

/**
 * Derive the IPTV `seekRangeMode` from forward/backward permission flags:
 * NONE, FORWARD_ONLY, BACKWARD_ONLY, or FULL, so the seek bar only exposes
 * the direction(s) the restrictions allow.
 *
 * TMIS does not use this — its range is either NONE or FULL and follows the
 * seekable window rather than the restrictions (see deriveModeEligibility).
 */
function deriveSeekRangeMode(
  allowForward: boolean,
  allowBackward: boolean
): SeekRangeMode {
  if (allowForward && allowBackward) {
    return "full";
  }
  if (!allowForward && allowBackward) {
    return "backward-only";
  }
  if (allowForward && !allowBackward) {
    return "forward-only";
  }
  return "none";
}

/**
 * Derive the mode-matrix eligibility for live / timeshift playback, i.e. the
 * state the seek bar and trick-play run on, plus the action-button rules that
 * apply while the *live* stream is playing.
 *
 * `deriveEligibility` wraps this and layers the SOCU action-button override on
 * top — see there for the full contract.
 *
 * Behavior matrix:
 *
 * **TMIS / Thumbnail mode** (`config.enableThumbnailSeek`):
 *   The seek-bar range follows what can be reached, not the restrictions —
 *   those only gate interaction (skip / hold / scrub / speed-cycle):
 *   - `startOverAvailable=true`:
 *       Full range from programme start; Start Over always available;
 *       Go LIVE always available.
 *   - `startOverAvailable=false` (always the case for nDVR / VOD):
 *       Full range when `hasSeekableWindow`, otherwise none. On live that is
 *       the "timeshift only" bar, spanning buffer start to the live edge.
 *       Restart (in place) is available so the Start Over button can either
 *       restart or, when rewind is restricted, explain why it cannot. Go LIVE
 *       follows `allowFastForward`.
 *
 * **IPTV / I-frame / Emulated mode** (everything else):
 *   - `startOverAvailable=true`:
 *       Trick-play follows `trickModeRestrictions`. Start Over switches
 *       to dedicated SOCU stream. Go LIVE follows `allowFastForward`.
 *   - `startOverAvailable=false`:
 *       Trick-play follows `trickModeRestrictions`. Start Over button
 *       seeks to buffer start (same stream) — enabled only if
 *       `allowRewind=true`. Go LIVE follows `allowFastForward`.
 *
 * **Restrictions missing / unreachable** (`restrictions === null`):
 *   Returns an all-disabled eligibility; on TMIS `hasSeekableWindow` is
 *   ignored, so the range is FULL only when Start Over is available, and
 *   restart stays unavailable. The caller is also expected to
 *   call `tpm.setRestrictionEnabled(false)` so the seek bar renders
 *   non-interactive per TP-AP-4.
 *
 * @param flags - Session-level eligibility inputs.
 * @param config - Active trick-play configuration (mode-selection inputs).
 * @returns The derived eligibility object.
 */
function deriveModeEligibility(
  flags: ITrickPlaySessionFlags,
  config: ITrickPlayConfig
): ITrickPlayEligibility {
  let restrictions = flags.trickModeRestrictions;

  // No restrictions data → all disabled.
  if (restrictions === null) {
    restrictions = {
      allowFastForward: false,
      allowRewind: false,
      allowPause: false
    };
  }

  const isThumbnailMode: boolean = config.enableThumbnailSeek;

  // ── TMIS: Start Over Disabled → seek bar follows the seekable window ──
  // FULL when the player has a window to seek in (on live, the timeshift
  // buffer), NONE when it does not; interactivity is governed by the flags.
  // Missing restrictions keep the all-restricted result: the window signal is
  // ignored (NONE) and restart stays off.
  if (isThumbnailMode && !flags.startOverAvailable) {
    const allowForward: boolean = restrictions.allowFastForward;
    const allowBackward: boolean = restrictions.allowRewind;
    const anyTrickAllowed: boolean = allowForward || allowBackward;
    const restrictionsKnown: boolean = flags.trickModeRestrictions !== null;
    // Without a window signal, fall back to proxying it by the restrictions.
    const hasSeekableWindow: boolean =
      restrictionsKnown && flags.hasSeekableWindow !== undefined
        ? flags.hasSeekableWindow
        : anyTrickAllowed;

    return {
      trickPlayAllowed: anyTrickAllowed,
      startOverAvailable: false,
      // In-place restart, offered even when rewind is restricted so the
      // Start Over button can show the rewind restriction message.
      restartAvailable: restrictionsKnown,
      goLiveAvailable: allowForward,
      allowForward,
      allowBackward,
      allowPause: restrictions.allowPause,
      seekRangeMode: hasSeekableWindow ? "full" : "none"
    };
  }

  // ── Common restriction-based derivation ──
  const allowForward: boolean = restrictions.allowFastForward;
  const allowBackward: boolean = restrictions.allowRewind;
  const anyTrickAllowed: boolean = allowForward || allowBackward;

  // ── TMIS: Start Over Enabled ──
  // Restrictions gate skip/hold/speed-cycle only.
  // Seek bar always shows full range; interactivity depends on restrictions
  // (non-interactive when both FF/RW false).
  if (isThumbnailMode && flags.startOverAvailable) {
    return {
      trickPlayAllowed: anyTrickAllowed,
      startOverAvailable: true,
      restartAvailable: false,
      goLiveAvailable: true,
      allowForward,
      allowBackward,
      allowPause: restrictions.allowPause,
      seekRangeMode: "full"
    };
  }

  // ── IPTV: seek bar range is restricted by FF/RW flags ──
  const seekRangeMode: SeekRangeMode = deriveSeekRangeMode(
    allowForward,
    allowBackward
  );

  // ── IPTV: Start Over Disabled → same-stream restart from buffer ──
  // startOverAvailable=false (no dedicated stream).
  // restartAvailable=allowBackward (same-stream seek to buffer start).
  if (!isThumbnailMode && !flags.startOverAvailable) {
    return {
      trickPlayAllowed: anyTrickAllowed,
      startOverAvailable: false,
      restartAvailable: allowBackward,
      goLiveAvailable: allowForward,
      allowForward,
      allowBackward,
      allowPause: restrictions.allowPause,
      seekRangeMode
    };
  }

  // ── IPTV: Start Over Enabled ──
  // Dedicated SOCU stream available. Seek bar restricted by FF/RW.
  return {
    trickPlayAllowed: anyTrickAllowed,
    startOverAvailable: true,
    restartAvailable: false,
    goLiveAvailable: allowForward,
    allowForward,
    allowBackward,
    allowPause: restrictions.allowPause,
    seekRangeMode
  };
}

/**
 * Derive `ITrickPlayEligibility` from session-level inputs.
 *
 * Runs the live / timeshift mode matrix (see {@link deriveModeEligibility}) and
 * then, when SOCU is playing, overrides the two action-button flags.
 *
 * **SOCU action buttons** (`flags.isSocuPlaying`) — applies identically to Start
 * Over and Catch Up, on both media planes:
 *   - `restartAvailable: true` — the Start Over button seeks to the start of the
 *     SOCU stream (the programme start), which is always possible once the
 *     programme is playing from the recording host.
 *   - `goLiveAvailable: true` — Go LIVE switches to the live programme on the
 *     same channel. It is a stream switch, not a forward seek, so it is not
 *     gated on `allowFastForward`.
 *
 * Everything the seek bar and trick-play depend on — `trickPlayAllowed`,
 * `seekRangeMode`, `allowForward`, `allowBackward`, `allowPause` and
 * `startOverAvailable` — is passed through from the mode matrix untouched, so
 * `trickModeRestrictions` still governs seeking exactly as it does for live.
 *
 * @param flags - Session-level eligibility inputs.
 * @param config - Active trick-play configuration (mode-selection inputs).
 * @returns The derived eligibility object.
 */
export function deriveEligibility(
  flags: ITrickPlaySessionFlags,
  config: ITrickPlayConfig
): ITrickPlayEligibility {
  if (flags.isSocuPlaying) {
    const base: ITrickPlayEligibility = deriveModeEligibility(flags, config);
    return { ...base, restartAvailable: true, goLiveAvailable: true };
  }

  const isNdvrOrVodPlaying: boolean =
    !!flags.isNdvrPlaying || !!flags.isVodPlaying;

  const base: ITrickPlayEligibility = deriveModeEligibility(
    isNdvrOrVodPlaying ? { ...flags, startOverAvailable: false } : flags,
    config
  );

  if (isNdvrOrVodPlaying) {
    return { ...base, restartAvailable: true, goLiveAvailable: false };
  }

  return base;
}
